import { z } from "zod";

// parses string JSONS and returns the string if it fails
export function safeParseJSON<T>(string: string): string | T {
	try {
		return JSON.parse(string);
	} catch {
		return string;
	}
}

// Wrapper types that need unwrapping to get to the core type
const WRAPPER_TYPES = ["optional", "nullable", "default", "catch"];

// Type guard to check if a def has innerType property
type DefWithInnerType = {
	type: string;
	innerType: z.ZodType;
};

function hasInnerType(def: any): def is DefWithInnerType {
	return def && "innerType" in def;
}

function resolveCoreType(zodType: z.ZodType): z.ZodType {
	let currentType: any = zodType;

	// Unwrap recursively until the core type is found
	while (
		currentType?._zod?.def &&
		WRAPPER_TYPES.includes(currentType._zod.def.type) &&
		hasInnerType(currentType._zod.def)
	) {
		currentType = currentType._zod.def.innerType;
	}

	return currentType;
}

// converts a zod schema to a zod schema that accepts a URLSearchParams object
export function makeSearchParamsObjSchema<Schema extends z.ZodObject<z.ZodRawShape>>(schema: Schema) {
	return z
		.instanceof(URLSearchParams)
		.transform((params) => searchParamsToValuesWithSchema(params, schema))
		.pipe(schema);
}

// converts a URLSearchParams object to a record of values
export function searchParamsToValuesWithSchema<Schema extends z.ZodObject<z.ZodRawShape>>(
	searchParams: URLSearchParams,
	schema: Schema
): Record<string, any> {
	const shape = schema.shape;

	const result = Array.from(searchParams.keys()).reduce(
		(record, key) => {
			const schemaType: any = resolveCoreType(shape[key] as z.ZodType); // Resolve to the core type

			let value: any = null;

			switch (schemaType?._zod?.def?.type) {
				case "array":
					value = searchParams.getAll(key).flatMap((value) => {
						if (value === "") {
							return [];
						}
						// Split comma-separated values if the schema expects an array
						const val = value.split(",");
						return val;
					});
					break;

				case "boolean":
					value = searchParams.get(key) === "true";
					break;

				case "number":
					value = Number(searchParams.get(key));
					break;

				case "date": {
					const dateString = searchParams.get(key);
					value = dateString ? new Date(dateString) : null;
					break;
				}
				default:
					value = searchParams.get(key);
					break;
			}

			// Assign values based on whether the schema expects an array
			record[key] = value;

			return record;
		},
		{} as Record<string, any>
	);

	return result;
}

// coerces the sting array from URL Params to a true array
export function coerceToArray<Schema extends z.ZodArray<z.ZodType>>(schema: Schema) {
	return z.union([z.any().array(), z.any().transform((x) => [x])]).pipe(schema);
}
